import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { app } from '../../firebase';

import NavbarDashboard from '../Dashboard/NavbarDashboard';
import {
  Box, Select, MenuItem, TextField, FormControl, InputLabel,
  Button, Chip, Grid, Typography, Skeleton
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ResponsiveDrawer from '../Dashboard/Sidebar';

const firestore = getFirestore(app);

// ─── Year options — same logic as TdsDetails, computed once ──────────────────
const generateYearOptions = () => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const startYear = currentMonth >= 4 ? currentYear : currentYear - 1;

  return [
    {
      value: startYear.toString(),
      label: `${startYear}-${String(startYear + 1).slice(-2)}`,
      firestoreKey: `tdsData${startYear}`
    }
  ];
};

const YEAR_OPTIONS = generateYearOptions(); // computed once at module level

// ─── Parallel fetch — all 4 collections at once ───────────────────────────────
const fetchAllCollections = async () => {
  const [usersSnap, tdsStateSnap, tdsFormSnap, tdsDetailsSnap] = await Promise.all([
    getDocs(collection(firestore, 'users')),
    getDocs(collection(firestore, 'tds_state')),
    getDocs(collection(firestore, 'tds_forms')),
    getDocs(collection(firestore, 'tds_details'))
  ]);

  // Map lookups — O(1) instead of O(n) find() per user
  const tdsStateMap   = new Map(tdsStateSnap.docs.map(d => [d.id, d.data()]));
  const tdsDetailsMap = new Map(tdsDetailsSnap.docs.map(d => [d.id, d.data()]));
  const tdsFormMap    = new Map(
    tdsFormSnap.docs.map(d => {
      const data = d.data();
      // tds_forms uses userId field, not doc id
      return [data.userId ?? d.id, { ...data, timestamp: data.timestamp?.toDate() ?? null }];
    })
  );

  const merged = usersSnap.docs.map(d => {
    const id      = d.id;
    const user    = { id, ...d.data() };
    const state   = tdsStateMap.get(id)   ?? {};
    const details = tdsDetailsMap.get(id) ?? {};
    const form    = tdsFormMap.get(id)    ?? {};

    // Dynamically compute TDS per year and grand total
    const yearData = {};
    let totalTds = 0;

    YEAR_OPTIONS.forEach(({ value, firestoreKey }) => {
      const rows = details[firestoreKey] ?? [];
      yearData[firestoreKey] = rows;
      totalTds += rows.reduce((sum, item) => sum + Number(item.tds || 0), 0);
    });

    return { ...user, tdsState: state, ...form, ...yearData, totalTds };
  });

  // Sort by timestamp — latest first
  return merged.sort((a, b) => {
    const ta = a.timestamp ? +a.timestamp : 0;
    const tb = b.timestamp ? +b.timestamp : 0;
    return tb - ta;
  });
};

// ─── Skeleton row ─────────────────────────────────────────────────────────────
const SkeletonRow = ({ cols }) => (
  <tr>
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i}><Skeleton variant="text" /></td>
    ))}
  </tr>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const PaymentStatus = ({ userPermissions }) => {
  const [users, setUsers]                                   = useState([]);
  const [loading, setLoading]                               = useState(true);
  const [searchTerm, setSearchTerm]                         = useState('');
  const [debouncedSearch, setDebouncedSearch]               = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter]       = useState('all');
  const [paymentConfirmationFilter, setPaymentConfirmationFilter] = useState('all');
  const [tdsYearFilter, setTdsYearFilter]                   = useState('all');
  const [showFilters, setShowFilters]                       = useState(false);

  // ── Debounce search (300ms) ──
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── Fetch ──
  const fetchData = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const data = await fetchAllCollections();
      setUsers(data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
    const id = setInterval(() => fetchData(false), 5 * 60 * 1000); // 5 min
    return () => clearInterval(id);
  }, [fetchData]);

  // ── Memoized filter ──
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      if (debouncedSearch) {
        const s = debouncedSearch.toLowerCase();
        if (!user.name?.toLowerCase().includes(s) && !user.email?.toLowerCase().includes(s)) return false;
      }
      if (paymentStatusFilter !== 'all') {
        const isPaid = !!user.tdsState?.paymentstatus;
        if (paymentStatusFilter === 'complete'   && !isPaid) return false;
        if (paymentStatusFilter === 'incomplete' &&  isPaid) return false;
      }
      if (paymentConfirmationFilter !== 'all') {
        const passed = user.tdsState?.paymentConfirmation === 'true';
        if (paymentConfirmationFilter === 'passed' && !passed) return false;
        if (paymentConfirmationFilter === 'failed' &&  passed) return false;
      }
      if (tdsYearFilter !== 'all') {
        const key  = `tdsData${tdsYearFilter}`;
        if (!user[key]?.length) return false;
      }
      return true;
    });
  }, [users, debouncedSearch, paymentStatusFilter, paymentConfirmationFilter, tdsYearFilter]);

  // ── Memoized stats ──
  const stats = useMemo(() => {
    const yearCounts = {};
    YEAR_OPTIONS.forEach(({ value, firestoreKey }) => {
      yearCounts[value] = users.filter(u => u[firestoreKey]?.length > 0).length;
    });
    return {
      complete:   users.filter(u => u.tdsState?.paymentstatus).length,
      incomplete: users.filter(u => !u.tdsState?.paymentstatus).length,
      passed:     users.filter(u => u.tdsState?.paymentConfirmation === 'true').length,
      failed:     users.filter(u => u.tdsState?.paymentConfirmation !== 'true').length,
      yearCounts
    };
  }, [users]);

  // ── Optimistic status update ──
  const handleConfirmationChange = async (userId, value) => {
    setUsers(prev => prev.map(u =>
      u.id === userId ? { ...u, tdsState: { ...u.tdsState, paymentConfirmation: value } } : u
    ));
    try {
      await updateDoc(doc(firestore, 'tds_state', userId), { paymentConfirmation: value });
    } catch (err) {
      console.error('Update error:', err);
      // Rollback on failure
      fetchData(false);
    }
  };

  // Table column count: Sr + Date + Name + Email + Phone + (years) + Total + PayStatus + PayConfirm
  const totalCols = 6 + YEAR_OPTIONS.length + 3;

  return (
    <div>
      <NavbarDashboard />
      <Box height={10} />
      <Box sx={{ display: 'flex' }}>
        <ResponsiveDrawer userPermissions={userPermissions} />
        <Box component="main" sx={{ flexGrow: 1, p: 9, minWidth: 0 }}>
          <div className="container">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h4" gutterBottom>Payment Status</Typography>
              <Button variant="outlined" startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(v => !v)}>
                Filters
              </Button>
            </Box>

            {/* Search + Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <TextField label="Search by Name or Email" variant="outlined" fullWidth
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </Grid>
              {showFilters && (
                <>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Payment Status</InputLabel>
                      <Select value={paymentStatusFilter} label="Payment Status"
                        onChange={e => setPaymentStatusFilter(e.target.value)}>
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="complete">Complete</MenuItem>
                        <MenuItem value="incomplete">Incomplete</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>Confirmation</InputLabel>
                      <Select value={paymentConfirmationFilter} label="Confirmation"
                        onChange={e => setPaymentConfirmationFilter(e.target.value)}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="passed">Passed</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth>
                      <InputLabel>TDS Year</InputLabel>
                      <Select value={tdsYearFilter} label="TDS Year"
                        onChange={e => setTdsYearFilter(e.target.value)}>
                        <MenuItem value="all">All Years</MenuItem>
                        {YEAR_OPTIONS.map(o => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}
            </Grid>

            {/* Filter Chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {[
                { label: `Complete: ${stats.complete}`,   color:'success', key:'complete',   filter: paymentStatusFilter,       set: setPaymentStatusFilter },
                { label: `Incomplete: ${stats.incomplete}`,color:'error', key:'incomplete',  filter: paymentStatusFilter,       set: setPaymentStatusFilter },
                { label: `Passed: ${stats.passed}`,       color:'success', key:'passed',     filter: paymentConfirmationFilter, set: setPaymentConfirmationFilter },
                { label: `Failed: ${stats.failed}`,       color:'error',   key:'failed',     filter: paymentConfirmationFilter, set: setPaymentConfirmationFilter },
              ].map(c => (
                <Chip key={c.key} label={c.label} color={c.color} clickable
                  variant={c.filter === c.key ? 'filled' : 'outlined'}
                  onClick={() => c.set(v => v === c.key ? 'all' : c.key)} />
              ))}
              {/* Dynamic year chips */}
              {YEAR_OPTIONS.map(o => (
                <Chip key={o.value}
                  label={`${o.label}: ${stats.yearCounts[o.value] ?? 0}`}
                  color="primary" clickable
                  variant={tdsYearFilter === o.value ? 'filled' : 'outlined'}
                  onClick={() => setTdsYearFilter(v => v === o.value ? 'all' : o.value)} />
              ))}
            </Box>

            {/* Table */}
            <div className="table-wrapper">
                <table className="responsive-table">
                  <thead>
                    <tr>
                      <th className="sticky-header">Sr No.</th>
                      <th className="sticky-header">Date & Time</th>
                      <th className="sticky-header sticky-column">User Name</th>
                      <th className="sticky-header">Email ID</th>
                      <th className="sticky-header">Phone No.</th>
                      {/* Dynamic year columns */}
                      {YEAR_OPTIONS.map(o => <th key={o.value} className="sticky-header">TDS {o.label}</th>)}
                      <th className="sticky-header">Total TDS</th>
                      <th className="sticky-header">Payment Status</th>
                      <th className="sticky-header">Payment Confirmation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading
                      ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cols={totalCols} />)
                      : filteredUsers.map((user, index) => (
                        <tr key={user.id} className={index % 2 === 0 ? 'even' : 'odd'}>
                          <td>{index + 1}</td>
                          <td>{user.timestamp ? new Date(user.timestamp).toLocaleString() : 'NA'}</td>
                          <td className="sticky-column">{user.name}</td>
                          <td>{user.email}</td>
                          <td>{user.phone}</td>
                          {/* Dynamic year data cells */}
                          {YEAR_OPTIONS.map(o => (
                            <td key={o.value}>
                              {(user[o.firestoreKey] ?? []).length > 0
                                ? user[o.firestoreKey].map((tds, i) => (
                                  <div key={i}>{`${tds.company} | ₹${tds.income} | TDS: ${tds.tds}`}</div>
                                ))
                                : <span style={{ color: '#aaa' }}>—</span>
                              }
                            </td>
                          ))}
                          <td><strong>₹{user.totalTds}</strong></td>
                          <td style={{
                            backgroundColor: user.tdsState?.paymentstatus ? '#03C03C' : '#EF0107',
                            color: 'white'
                          }}>
                            {user.tdsState?.paymentstatus ? 'Complete' : 'Incomplete'}
                          </td>
                          <td>
                            <Select
                              style={{
                                color: 'white',
                                backgroundColor: user.tdsState?.paymentConfirmation === 'true' ? '#03C03C' : '#EF0107'
                              }}
                              value={user.tdsState?.paymentConfirmation || 'false'}
                              onChange={e => handleConfirmationChange(user.id, e.target.value)}
                            >
                              <MenuItem value="true">Passed</MenuItem>
                              <MenuItem value="false">Failed</MenuItem>
                            </Select>
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
            </div>

            {!loading && filteredUsers.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                  No users match the current filters
                </Typography>
              </Box>
            )}
          </div>
        </Box>
      </Box>
    </div>
  );
};

export default PaymentStatus;

const css = `
.even { background-color: #f2f2f2; }
.odd  { background-color: #ffffff; }

.sticky-header {
  position: sticky;
  top: 0;
  background-color: lightgreen !important;
  z-index: 100;
  box-shadow: 0 2px 2px -1px rgba(0, 0, 0, 0.4);
}

.table-wrapper { overflow-x: auto; max-height: calc(100vh - 240px); position: relative; }
.responsive-table { width: 100%; border-collapse: collapse; }
.responsive-table th,
.responsive-table td {
  padding: 12px;
  text-align: left;
  border: 1px solid #ddd;
  white-space: normal;
  word-wrap: break-word;
  max-width: 200px;
}

/* Freeze User Name column (including header) */
.sticky-column {
  position: sticky;
  left: 0;
  background-color: inherit;
  z-index: 11;
}

.sticky-header.sticky-column {
  background-color: lightgreen !important;
  z-index: 101;
}

/* Ensure sticky column background matches row */
.even .sticky-column {
  background-color: #f2f2f2;
}

.odd .sticky-column {
  background-color: #ffffff;
}
`;
document.head.insertAdjacentHTML('beforeend', `<style>${css}</style>`);